import { marked } from "marked";

const renderer = new marked.Renderer();
renderer.image = ({ href, text }) => {
  return `<img src="${href}" alt="${text}" style="max-width:100%;" />`;
};
marked.use({ renderer });
marked.setOptions({ breaks: true });

marked.use({
  hooks: {
    postprocess(html) {
      return html
        .replaceAll("<img ", '<img loading="lazy" decoding="async" ')
        .replaceAll("<iframe ", '<iframe loading="lazy" ')
        .replaceAll("<video ", '<video preload="metadata" ')
        .replaceAll("<pre>", '<pre class="glass-card">');
    },
  },
});

const VIDEO_TAG = (url) =>
  `<video src="${url}" controls playsinline preload="metadata" style="max-width:100%;border-radius:4px;"></video>`;

export function parseReadme(md, media = {}) {
  const local = (url) => media[url.replaceAll("&amp;", "&")];
  const html = marked.parse(md).replace(/<img\b[^>]*?>/gi, (tag) => {
    const src = tag.match(/src=["']([^"']+)["']/)?.[1];
    const m = src && local(src);
    if (!m) return tag;
    let out = tag.replace(src, m.src);
    if (m.w && !/\bwidth=/.test(tag))
      out = out.replace(/<img\b/, `<img width="${m.w}" height="${m.h}"`);
    return out;
  });
  return html
    .replace(
      /<a[^>]*href="(https:\/\/github\.com\/user-attachments\/assets\/[^"]+)"[^>]*>[\s\S]*?<\/a>/g,
      (_, url) => VIDEO_TAG(local(url)?.src ?? url)
    )
    .replace(
      /(?<!=["'])(https:\/\/github\.com\/user-attachments\/assets\/[a-f0-9-]+)/g,
      (url) => VIDEO_TAG(local(url)?.src ?? url)
    );
}

/**
 * Adds anchor ids to top-level h1 and h2 headings and lists them for a contents box.
 * Receives rendered README html and returns { html, headings } with headings as { id, text }.
 */
export function addHeadingAnchors(html) {
  const headings = [];
  const used = new Set();
  const out = html.replace(/<(h[12])>([\s\S]*?)<\/\1>/g, (_, tag, inner) => {
    const text = inner.replace(/<[^>]+>/g, "").replace(/&amp;/g, "&").replace(/&#39;/g, "'").replace(/&quot;/g, '"').trim();
    const base = text.toLowerCase().normalize("NFKD").replace(/[^\p{L}\p{N}]+/gu, "-").replace(/^-+|-+$/g, "") || "section";
    let id = base;
    for (let n = 2; used.has(id); n++) id = `${base}-${n}`;
    used.add(id);
    headings.push({ id, text });
    return `<${tag} id="${id}">${inner}</${tag}>`;
  });
  return { html: out, headings };
}

export function stripLeadingH1(md) {
  return md.replace(/^\s*#\s+[^\n]+\n?/, "");
}

export const README_PROSE = `prose text-foreground
  [&_h1]:font-black [&_h1]:text-3xl md:[&_h1]:text-4xl [&_h1]:tracking-tight [&_h1]:mb-5 [&_h1]:mt-12 [&_h1]:pb-3 [&_h1]:border-b [&_h1]:border-border
  [&_h2]:font-bold [&_h2]:text-2xl [&_h2]:tracking-tight [&_h2]:mb-3 [&_h2]:mt-8 [&_h2]:border-l-2 [&_h2]:border-purple [&_h2]:pl-3
  [&_h3]:text-xl [&_h3]:font-semibold [&_h3]:mb-2 [&_h3]:mt-6
  [&_p]:mb-5 [&_p]:leading-relaxed
  [&_a]:text-foreground [&_a]:underline [&_a]:underline-offset-2 [&_a]:decoration-purple/60 [&_a:hover]:decoration-purple
  [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-5
  [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mb-5
  [&_li]:mb-1 [&_li]:marker:text-purple
  [&_pre]:p-4 [&_pre]:overflow-x-auto [&_pre]:mb-4
  [&_pre_code]:!bg-transparent [&_pre_code]:p-0
  [&_code]:bg-muted [&_code]:px-1 [&_code]:rounded [&_code]:text-sm [&_code]:font-mono [&_code]:text-foreground
  [&_blockquote]:border-l-2 [&_blockquote]:border-purple/50 [&_blockquote]:pl-4 [&_blockquote]:italic
  [&_hr]:border-border [&_hr]:my-6
  [&_img]:max-w-full [&_img]:h-auto [&_img]:my-8
  [&_video]:w-full [&_video]:aspect-video [&_video]:my-8 [&_iframe]:my-8
  [&_table]:w-full [&_table]:border-collapse [&_table]:mb-6 [&_table]:text-sm
  [&_thead]:border-b [&_thead]:border-border
  [&_th]:text-left [&_th]:py-2 [&_th]:px-4 [&_th]:font-semibold [&_th]:text-foreground
  [&_td]:py-2 [&_td]:px-4 [&_td]:border-b [&_td]:border-border/50
  [&_tr:last-child_td]:border-b-0
  [&_tbody_tr:hover]:bg-muted/40`;
